import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MovieIcon from "@mui/icons-material/Movie";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import YouTubeIcon from "@mui/icons-material/YouTube";
import { useGetNotesQuery } from "../../../../../app/notesApiSlice";
import { stripHtmlTags } from "../../../../../helpers/htmlToText";

const NotesOverview = () => {
  const navigate = useNavigate();
  const [sortBy, setSortBy] = React.useState("date");
  const [page, setPage] = React.useState(1);
  const [filterSubject, setFilterSubject] = React.useState("all");
  const [filterLevel, setFilterLevel] = React.useState("all");
  const itemsPerPage = 10;

  const { data, isLoading } = useGetNotesQuery();
  const notes = React.useMemo(() => data?.notes || [], [data]);

  const uniqueSubjects = React.useMemo(() => {
    return [...new Set(notes.map((note) => note.subject).filter(Boolean))].sort();
  }, [notes]);

  const uniqueLevels = React.useMemo(() => {
    return [...new Set(notes.map((note) => note.level).filter(Boolean))].sort();
  }, [notes]);

  const filteredNotes = React.useMemo(() => {
    return notes
      .filter((note) => filterSubject === "all" || note.subject === filterSubject)
      .filter((note) => filterLevel === "all" || note.level === filterLevel);
  }, [notes, filterSubject, filterLevel]);

  const sortedNotes = React.useMemo(() => {
    return [...filteredNotes].sort((a, b) => {
      if (sortBy === "date") {
        return new Date(b.updated_at) - new Date(a.updated_at);
      }
      if (sortBy === "subject") {
        return a.subject.localeCompare(b.subject);
      }
      if (sortBy === "level") {
        return a.level.localeCompare(b.level);
      }
      return a.topic.localeCompare(b.topic);
    });
  }, [filteredNotes, sortBy]);

  const paginatedNotes = React.useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    return sortedNotes.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedNotes, page]);

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeSort = (event) => {
    setSortBy(event.target.value);
    setPage(1);
  };

  const handleFilterSubjectChange = (event) => {
    setFilterSubject(event.target.value);
    setPage(1);
  };

  const handleFilterLevelChange = (event) => {
    setFilterLevel(event.target.value);
    setPage(1);
  };

  const openNote = (note) => {
    navigate(`view/${note.id}`, { state: { note } });
  };

  const getResourceIcon = (type) => {
    if (type === "pdf") return <PictureAsPdfIcon sx={{ fontSize: 16 }} />;
    if (type === "youtube") return <YouTubeIcon sx={{ fontSize: 16 }} />;
    return <MovieIcon sx={{ fontSize: 16 }} />;
  };

  const getResourceLabel = (type) => {
    if (type === "pdf") return "PDF";
    if (type === "youtube") return "YouTube";
    return "Video";
  };

  const totalNotes = sortedNotes.length;
  const startIndex = (page - 1) * itemsPerPage + 1;
  const endIndex = Math.min(page * itemsPerPage, totalNotes);

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return "Invalid date";
    }
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  return (
    <Box>
      <Box sx={{ marginTop: "2em" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
            marginBottom: "1em",
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: "1rem", md: "1.25rem" },
              fontWeight: 700,
              letterSpacing: "-1px",
            }}
          >
            Recent Notes
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Select value={sortBy} onChange={handleChangeSort} size="small" sx={{ minWidth: 140 }}>
              <MenuItem value="date">Sort by: Date</MenuItem>
              <MenuItem value="topic">Sort by: Topic</MenuItem>
              <MenuItem value="subject">Sort by: Subject</MenuItem>
              <MenuItem value="level">Sort by: Level</MenuItem>
            </Select>
            <Select value={filterSubject} onChange={handleFilterSubjectChange} size="small" sx={{ minWidth: 160 }}>
              <MenuItem value="all">All Subjects</MenuItem>
              {uniqueSubjects.map((subject) => (
                <MenuItem key={subject} value={subject}>
                  {subject}
                </MenuItem>
              ))}
            </Select>
            <Select value={filterLevel} onChange={handleFilterLevelChange} size="small" sx={{ minWidth: 140 }}>
              <MenuItem value="all">All Levels</MenuItem>
              {uniqueLevels.map((level) => (
                <MenuItem key={level} value={level}>
                  {level}
                </MenuItem>
              ))}
            </Select>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate("new-note")} sx={{ textTransform: "none" }}>
              Add Note
            </Button>
          </Box>
        </Box>

        {isLoading && <LinearProgress />}

        {paginatedNotes.length === 0 && !isLoading && (
          <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
            <Typography variant="body1">No notes found</Typography>
          </Box>
        )}

        <Stack spacing={2}>
          {paginatedNotes.map((note) => {
            const resources = (note.resources || []).filter((resource) =>
              ["pdf", "video", "youtube"].includes(resource.type)
            );

            return (
              <Card
                key={note.id}
                onClick={() => openNote(note)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    openNote(note);
                  }
                }}
                role="button"
                tabIndex={0}
                sx={{
                  cursor: "pointer",
                  border: (theme) => `1px solid ${theme.palette.mode === "dark" ? "#333" : "#E0E0E0"}`,
                  transition: "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
                  "&:hover": {
                    borderColor: "primary.main",
                    boxShadow: (theme) => theme.palette.mode === "dark" ? "0 10px 24px rgba(0,0,0,0.25)" : "0 10px 24px rgba(16,24,40,0.08)",
                    transform: "translateY(-1px)",
                  },
                  "&:focus-visible": {
                    outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: 2,
                  },
                }}
              >
                <CardContent>
                  <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.5} sx={{ mb: 1.5 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.75 }}>
                        {note.topic || "Untitled note"}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
                        {note.subject && <Chip label={note.subject} size="small" variant="outlined" />}
                        {note.level && <Chip label={note.level} size="small" variant="outlined" />}
                      </Stack>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                      Updated {formatDate(note.updated_at)}
                    </Typography>
                  </Stack>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      lineHeight: 1.7,
                      mb: resources.length ? 2 : 0,
                    }}
                  >
                    {stripHtmlTags(note.body)}
                  </Typography>

                  {resources.length > 0 && (
                    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
                      {resources.map((resource) => (
                        <Chip
                          key={resource.id || resource.url}
                          icon={getResourceIcon(resource.type)}
                          label={resource.name || getResourceLabel(resource.type)}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>

        <Box sx={{ display: "flex", justifyContent: "space-between", marginTop: "2em", alignItems: "center" }}>
          <Typography variant="body2" color="text.secondary">
            Showing {paginatedNotes.length === 0 ? 0 : startIndex} to {endIndex} of {totalNotes} entries
          </Typography>
          <Pagination
            count={Math.ceil(sortedNotes.length / itemsPerPage)}
            page={page}
            onChange={handleChangePage}
            color="primary"
            shape="rounded"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default NotesOverview;
